import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

// =============================================================================
// AutoLey admin context
// -----------------------------------------------------------------------------
// Data layer for the AutoLey license-support dashboard. Talks to the
// autoley_admin_context package on the Go backend, mounted under both /admin
// and /master (dual access, like QR Create):
//
//   {API}/{admin|master}/autoley-admin/api/v1/autoley/...
//
// The backend reads the AutoLey license server's own database (accounts,
// licenses, devices, audit log). What it exposes:
//   • /overview         totals, licenses at their limit, duplicate installs,
//                       locked-out lawyers, activation/refusal timelines and the
//                       review queue (most urgent first)
//   • /licenses         every license with its account and review flags
//                       (?filter=needs_review|at_limit|duplicates|blocked)
//   • /licenses/{id}    every computer the license was activated on (with the
//                       activation IP), duplicate suspects, refused activations
//                       matched to the computer they look like, audit history
//   • /activity         activations, refusals and deactivations, newest first
//
// The two writes (both recorded in AutoLey's audit log with the admin's id):
//   • POST /devices/{id}/deactivate     {reason}  frees a stale record's slot.
//     The device's token stops working; if that computer IS still in use, the
//     app asks for the key again — so only free the record the evidence says
//     is stale.
//   • POST /licenses/{id}/max-devices   {from, max_devices, reason}  "grant
//     another activation" is from = current, max_devices = current + 1. A 409
//     means someone changed the allowance meanwhile: reload, don't retry.
//
// A 503 means the backend has no AUTOLEY_DATABASE_URL yet.
// =============================================================================

const AutoLeyContext = createContext();

export const useAutoLey = () => {
  const context = useContext(AutoLeyContext);
  if (!context) {
    throw new Error('useAutoLey must be used within an AutoLeyProvider');
  }
  return context;
};

export const AutoLeyProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://admin-golang-backend-8cdf9b481f22.herokuapp.com';

  // The package is mounted behind both /admin and /master. Pick the scope for
  // the current login.
  const scope = useCallback(() => {
    if (loginType === 'master') return 'master';
    if (loginType === 'admin') return 'admin';
    throw new Error('Invalid login type for AutoLey access');
  }, [loginType]);

  const base = useCallback(
    () => `${API_BASE_URL}/${scope()}/autoley-admin/api/v1/autoley`,
    [API_BASE_URL, scope],
  );

  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can manage AutoLey licenses');
    }
  }, [loginType]);

  const handleApiResponse = async (response) => {
    if (response.status === 204) {
      if (!response.ok) throw new Error('Request failed');
      return null;
    }
    let data = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }
    }
    if (!response.ok) {
      const msg = (data && (data.error || data.message)) || 'Request failed';
      const err = new Error(msg);
      err.status = response.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  const clearError = useCallback(() => setError(null), []);

  // Wrap loading/error handling around one request so each helper stays tiny.
  const run = useCallback(async (fn, fallbackMsg) => {
    validateAccess();
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      const msg = err.message || fallbackMsg;
      setError(msg);
      const wrapped = new Error(msg);
      wrapped.status = err.status;
      throw wrapped;
    } finally {
      setLoading(false);
    }
  }, [validateAccess]);

  const buildQuery = (options = {}, keys = []) => {
    const params = new URLSearchParams();
    keys.forEach((k) => {
      const v = options[k];
      if (v !== undefined && v !== null && v !== '') params.append(k, v);
    });
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  const get = useCallback((path, message) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${base()}${path}`)),
  message), [run, makeAuthenticatedRequest, base]);

  const post = useCallback((path, payload, message) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${base()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })),
  message), [run, makeAuthenticatedRequest, base]);

  // ── Reads ──────────────────────────────────────────────────────────────────
  const fetchOverview = useCallback((options = {}) =>
    get(`/overview${buildQuery(options, ['days'])}`, 'Failed to load the AutoLey overview'),
  [get]);

  const fetchLicenses = useCallback((options = {}) =>
    get(`/licenses${buildQuery(options, ['search', 'status', 'filter', 'limit', 'offset'])}`, 'Failed to load licenses'),
  [get]);

  const getLicense = useCallback((licenseId) =>
    get(`/licenses/${encodeURIComponent(licenseId)}`, 'Failed to load the license'),
  [get]);

  const fetchActivity = useCallback((options = {}) =>
    get(`/activity${buildQuery(options, ['kind', 'search', 'days', 'limit', 'offset'])}`, 'Failed to load activation activity'),
  [get]);

  // ── Writes ─────────────────────────────────────────────────────────────────

  // Frees the slot a stale device record holds.
  const deactivateDevice = useCallback((deviceId, { reason = '' } = {}) =>
    post(`/devices/${encodeURIComponent(deviceId)}/deactivate`, { reason }, 'Failed to deactivate the device'),
  [post]);

  // `from` is the allowance the admin was looking at; the server refuses (409)
  // if it has moved since, so a double click can't grant two slots.
  const setMaxDevices = useCallback((licenseId, { from, maxDevices, reason }) =>
    post(`/licenses/${encodeURIComponent(licenseId)}/max-devices`, { from, max_devices: maxDevices, reason },
      'Failed to change the allowance'),
  [post]);

  const value = {
    loading,
    error,
    clearError,
    fetchOverview,
    fetchLicenses,
    getLicense,
    fetchActivity,
    deactivateDevice,
    setMaxDevices,
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasAutoLeyAccess: loginType === 'admin' || loginType === 'master',
  };

  return (
    <AutoLeyContext.Provider value={value}>
      {children}
    </AutoLeyContext.Provider>
  );
};
